/** Геопозиция пользователя: автоматически и, если отказали, вручную по карте. */

import { useCallback, useEffect, useRef, useState } from 'react';

import type { LatLon } from '@cdf/shared';

export type GeoStatus =
  | 'idle'
  | 'locating'
  | 'ready'
  | 'denied'
  | 'unavailable'
  | 'manual';

export interface GeolocationState {
  status: GeoStatus;
  point: LatLon | null;
  /** Точность в метрах, если известна — показываем кружок вокруг точки. */
  accuracyM: number | null;
  message: string | null;
}

export interface GeolocationApi extends GeolocationState {
  /** Запросить позицию (первый раз или после отказа). */
  locate(): void;
  /** Ручная точка: тап по карте. */
  setManual(point: LatLon): void;
}

const OPTIONS: PositionOptions = {
  enableHighAccuracy: true,
  timeout: 12_000,
  maximumAge: 15_000,
};

function describeError(error: GeolocationPositionError): {
  status: GeoStatus;
  message: string;
} {
  switch (error.code) {
    case error.PERMISSION_DENIED:
      return {
        status: 'denied',
        message: 'Доступ к геопозиции запрещён. Разрешите его в настройках браузера или укажите точку на карте.',
      };
    case error.POSITION_UNAVAILABLE:
      return {
        status: 'unavailable',
        message: 'Не удалось определить позицию. Укажите точку старта на карте.',
      };
    default:
      return {
        status: 'unavailable',
        message: 'Позиция определяется слишком долго. Укажите точку старта на карте.',
      };
  }
}

const MANUAL_POINT_KEY = 'cdf.manual-point';

/**
 * Точка, заданная руками, переживает перезагрузку.
 *
 * Сервер помнит план по сессии и поднимает его сам, а вот стартовая точка
 * жила только в памяти вкладки. Из-за этого после обновления страницы экран
 * показывал готовый маршрут и одновременно просил разрешить геопозицию.
 */
function readManualPoint(): LatLon | null {
  try {
    const raw = window.localStorage.getItem(MANUAL_POINT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<LatLon>;
    if (
      typeof parsed.lat !== 'number' ||
      typeof parsed.lon !== 'number' ||
      !Number.isFinite(parsed.lat) ||
      !Number.isFinite(parsed.lon)
    ) {
      return null;
    }
    return { lat: parsed.lat, lon: parsed.lon };
  } catch {
    return null;
  }
}

export function useGeolocation(): GeolocationApi {
  const [state, setState] = useState<GeolocationState>(() => {
    const saved = readManualPoint();
    return saved
      ? { status: 'manual', point: saved, accuracyM: null, message: null }
      : { status: 'idle', point: null, accuracyM: null, message: null };
  });
  const watchId = useRef<number | null>(null);
  const manual = useRef(readManualPoint() !== null);

  const stopWatch = useCallback(() => {
    if (watchId.current !== null) {
      navigator.geolocation.clearWatch(watchId.current);
      watchId.current = null;
    }
  }, []);

  const locate = useCallback(() => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      setState({
        status: 'unavailable',
        point: null,
        accuracyM: null,
        message: 'Браузер не умеет определять позицию. Укажите точку на карте.',
      });
      return;
    }

    manual.current = false;
    setState((prev) => ({ ...prev, status: 'locating', message: null }));

    navigator.geolocation.getCurrentPosition(
      (position) => {
        if (manual.current) return;
        // Живая позиция точнее сохранённой — снимаем ручную точку, чтобы
        // она не всплыла при следующем запуске поверх настоящей.
        try {
          window.localStorage.removeItem(MANUAL_POINT_KEY);
        } catch {
          // Недоступное хранилище здесь ни на что не влияет.
        }
        setState({
          status: 'ready',
          point: {
            lat: position.coords.latitude,
            lon: position.coords.longitude,
          },
          accuracyM: position.coords.accuracy ?? null,
          message: null,
        });

        // Дальше только следим за точкой на карте — план по ней не пересчитываем.
        stopWatch();
        watchId.current = navigator.geolocation.watchPosition(
          (next) => {
            if (manual.current) return;
            setState((prev) => ({
              ...prev,
              status: 'ready',
              point: { lat: next.coords.latitude, lon: next.coords.longitude },
              accuracyM: next.coords.accuracy ?? null,
            }));
          },
          () => {
            /* Потеря сигнала при слежении — не повод показывать ошибку. */
          },
          OPTIONS,
        );
      },
      (error) => {
        if (manual.current) return;
        const described = describeError(error);
        setState({
          status: described.status,
          point: null,
          accuracyM: null,
          message: described.message,
        });
      },
      OPTIONS,
    );
  }, [stopWatch]);

  const setManual = useCallback(
    (point: LatLon) => {
      manual.current = true;
      stopWatch();
      try {
        window.localStorage.setItem(MANUAL_POINT_KEY, JSON.stringify(point));
      } catch {
        // Приватный режим или переполненное хранилище: точка просто не
        // переживёт перезагрузку, ронять из-за этого экран незачем.
      }
      setState({
        status: 'manual',
        point,
        accuracyM: null,
        message: null,
      });
    },
    [stopWatch],
  );

  useEffect(() => stopWatch, [stopWatch]);

  return { ...state, locate, setManual };
}
